import React from 'react';
import { CreditCard, Calendar } from 'lucide-react';
import { useSubscription } from '../../hooks/subscriptions/useSubscription';
import { formatDate } from '../../utils/dateUtils';
import { createStripePortalSession } from '../../services/stripe/stripeService';
import { UpgradeButton } from '../subscriptions/UpgradeButton';
import { toast } from 'react-hot-toast';

export function SubscriptionSettings() {
  const { subscription, isLoading } = useSubscription();
  const [isRedirecting, setIsRedirecting] = React.useState(false);

  const getSubscriptionDisplay = () => {
    if (!subscription) {
      return 'Free';
    }
    
    if (subscription.status === 'active' && new Date(subscription.current_period_end) > new Date()) {
      return subscription.plan.name;
    }
    
    return 'Free';
  };

  const handleManageSubscription = async () => {
    setIsRedirecting(true);
    try {
      if (!subscription?.stripe_customer_id) {
        toast.error('No active subscription found. Please subscribe first.');
        setIsRedirecting(false);
        return;
      }

      const url = await createStripePortalSession();
      window.location.href = url;
    } catch (error) {
      console.error('Error redirecting to portal:', error);
      setIsRedirecting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-button-primary mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-black">
          Subscription Plan
        </h3>
        <p className="mt-1 text-sm text-black">
          Manage your subscription and billing
        </p>
      </div>

      {subscription ? (
        <div className="bg-gray-50 dark:bg-background-cardLight rounded-lg p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-black mb-1">
                {subscription.plan?.name || 'Free'} Plan
              </h4>
              <p className="mt-1 text-sm text-black">
                {subscription.plan?.description}
              </p>
            </div>
            {subscription.cancel_at_period_end && (
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                Cancels at period end
              </span>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center text-sm text-black">
              <CreditCard className="h-5 w-5 mr-3" />
              ${subscription.plan?.price / 100}/{subscription.plan?.interval}
            </div>
            <div className="flex items-center text-sm text-black">
              <Calendar className="h-5 w-5 mr-3" />
              Current period ends: {formatDate(subscription.current_period_end)}
            </div>
            {subscription.cancel_at_period_end && (
              <div className="text-sm text-yellow-600 bg-yellow-50 px-3 py-1 rounded-full inline-block">
                Cancels at period end
              </div>
            )}
          </div>

          <button
            onClick={handleManageSubscription}
            disabled={isRedirecting || !subscription?.stripe_customer_id}
            className="w-full px-4 py-2 bg-black text-white rounded-lg hover:opacity-90 transition-colors flex items-center justify-center disabled:opacity-50"
          >
            {isRedirecting ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              subscription?.stripe_customer_id ? 'Manage Subscription' : 'No Active Subscription'
            )}
          </button>
        </div>
      ) : (
        <div className="bg-gray-50 dark:bg-background-cardLight rounded-lg p-6">
          <div className="text-center">
            <p className="text-black mb-4 space-y-2">
              <span className="block">You are currently on the {getSubscriptionDisplay()} plan.</span>
              {getSubscriptionDisplay() === 'Free' && (
                <span className="block">
                  Upgrade to access premium features and unlock all platform capabilities.
                </span>
              )}
            </p>
            <UpgradeButton>Upgrade to Premium</UpgradeButton>
          </div>
        </div>
      )}
    </div>
  );
}